import {
  AVAILABLE_BRAND_FEATURES,
  countDayUnitsBetween,
  GetListingV3PublicResponse,
  isDayBasedTimeFrame,
} from '@kouto/types';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import { useToastContext } from 'ToastProvider';
import { resetCartError } from 'actions/booking';
import { removeExperienceFromCart } from 'actions/cart';
import TrashIcon from 'assets/icon-trash';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import ResponsiveImage from 'components/common/ResponsiveImage/ResponsiveImage';
import {
  ClearButton,
  DimmedText,
  Flex,
  LinkText,
} from 'components/common/styled/common-styled';
import { Error, ErrorContainer } from 'components/theme/Form/Form';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { PaymentIntentError } from 'features/ShoppingCart/types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ICartExperience } from 'types/cart';
import { addMinutesToDate, formatDateForReq, TIME_FORMAT } from 'utils';
import { formatDateForDisplay, formatTimeInAMPM } from 'utils/dateTime';
import {
  getListingCoverAltText,
  getListingCoverUriObject,
} from 'utils/listings';
import { useDebugLog } from 'utils/debug';
import { convertIntervalToMinutes } from 'utils/formatDuration';
import HyattBrandFlag from 'assets/hyatt-brand-flag';
import { CartItemTotalDetailsWithQuote } from 'utils/cart/mergeServerQuote';
import {
  groupPricedUnitsByTier,
  resolveListingTimeFrame,
} from 'utils/cart/serverCartQuote';
import { deriveUnitLabelKey } from 'utils/continuousSelection';
import { validatePaymentIntentError } from '../../utils/validatePaymentIntentError';
import CartSummaryItemPrice from './CartSummaryItemPrice';
import CartSummaryItemUnitBreakdown from './CartSummaryItemUnitBreakdown';
import CartItemAddOns from './CartItemAddOns';
import {
  CartError,
  CartSummaryItemSection,
  CartSummaryItemTitle,
  ExperienceImage,
} from './styles';

type CartSummaryItemProps = {
  canDelete?: boolean;
  canEditParticipants?: boolean;
  cartItem: ICartExperience;
  cartItemTotalDetails: CartItemTotalDetailsWithQuote;
  isHyattEligible?: boolean;
  isPayingWithPoints?: boolean;
  listing?: GetListingV3PublicResponse;
  paymentIntentError?: PaymentIntentError | null;
};

const CartSummaryItem = ({
  canDelete = true,
  canEditParticipants = true,
  cartItem,
  cartItemTotalDetails,
  isHyattEligible,
  isPayingWithPoints,
  listing,
  paymentIntentError,
}: CartSummaryItemProps) => {
  const debug = useDebugLog();
  const { showToast } = useToastContext();
  const { t } = useTranslation();
  const brandId = useBrandId();
  const dispatch = useDispatch();
  const { locale } = useGlobalDateLocale();

  const { cartError } = useAppState(
    (state: Record<string, unknown>) => state.booking,
  );

  const isFlexibleDurationEnabled = useBrandToggleFeature(
    AVAILABLE_BRAND_FEATURES.FLEXIBLE_DURATION,
  );

  const getEndDateTime = () => {
    const mins = convertIntervalToMinutes(cartItem.sessionDuration);
    const endDateTime = addMinutesToDate(mins)(cartItem.sessionDateTime);
    return formatTimeInAMPM(endDateTime.toDate(), locale);
  };

  const cartItemErrorMessage = useMemo(() => {
    if (!paymentIntentError && !cartError) {
      return null;
    }
    if (paymentIntentError) {
      return validatePaymentIntentError(paymentIntentError, listing);
    }

    debug('error', { cartError });

    return cartError.find((item: Record<string, unknown>) => {
      if (!listing) return false;

      // Match by resourceGroupId against the listing's resource groups
      if (item.resourceGroupId && listing.resourceGroups) {
        return listing.resourceGroups.some(
          (rg) => rg.id === item.resourceGroupId,
        );
      }
      // Match by experienceId
      if (item.experienceId) {
        // Direct match: listing is a simple experience with a top-level experienceId
        if (
          listing.experienceId &&
          item.experienceId === listing.experienceId
        ) {
          return true;
        }
        // Nested match: experienceId lives within a resource group's experiences
        if (listing.resourceGroups) {
          return listing.resourceGroups.some((rg) =>
            rg.experiences?.some((exp) => exp.id === item.experienceId),
          );
        }
      }
      return false;
    });
  }, [debug, cartError, listing, paymentIntentError]);

  const coverAltText = useMemo(() => {
    return listing ? getListingCoverAltText(listing) : null;
  }, [listing]);

  // A continuous item's tiers were priced by the server; each carries the units and rate behind it.
  const quotedPriceTiers = useMemo(
    () =>
      cartItemTotalDetails.serverQuote
        ? groupPricedUnitsByTier(cartItemTotalDetails.serverQuote)
        : [],
    [cartItemTotalDetails.serverQuote],
  );
  const timeFrame = resolveListingTimeFrame(listing, cartItem.groupId);
  const isDayBased = isDayBasedTimeFrame(timeFrame);
  const durationMinutes = convertIntervalToMinutes(cartItem.sessionDuration);
  const endDateTime = addMinutesToDate(durationMinutes)(
    cartItem.sessionDateTime,
  ).format('YYYY-MM-DDTHH:mm:ss');
  const dayUnitsLabel = useMemo(() => {
    if (!isDayBased || !timeFrame) {
      return '';
    }
    const units = countDayUnitsBetween({
      endDateTime,
      startDateTime: cartItem.sessionDateTime,
      timeFrame,
    });
    return `${units} ${t(deriveUnitLabelKey(timeFrame, units))}`;
  }, [cartItem.sessionDateTime, endDateTime, isDayBased, t, timeFrame]);

  useEffect(() => {
    return () => {
      dispatch(resetCartError());
    };
  }, []);

  if (!listing) {
    return null;
  }

  const remove = () => {
    dispatch(
      removeExperienceFromCart({
        cartItemId: cartItem.cartItemId,
        persistMeta: {
          brandId,
        },
      }),
    );
    showToast({
      title: 'Success',
      message: 'Item removed from cart',
    });
  };

  const renderPrice = () => {
    const [sessionDate, sessionTime] = [
      formatDateForReq(cartItem.sessionDateTime),
      formatDateForReq(cartItem.sessionDateTime, TIME_FORMAT),
    ];

    return (
      <Flex style={{ flexDirection: 'column', alignItems: 'start' }} gap={8}>
        {canEditParticipants && (
          <LinkText
            to={`/e/${listing.id}/participants/${cartItem.cartItemId}?mode=${cartItem.bookingMode}&sessionDate=${sessionDate}&sessionTime=${sessionTime}`}
            size={14}
            aria-label={t('editParticipantsFor', {
              experienceTitle: cartItem.groupTitle || listing.title,
            })}
            className="cart-summary-item-edit-participants"
          >
            {t('editParticipants')}
          </LinkText>
        )}
        {cartItemTotalDetails.selectedPriceTiers.map((priceTier) => {
          const quotedTier = quotedPriceTiers.find(
            (tier) => tier.name === priceTier.name,
          );
          // A flat rate reads as "n nights x rate"; a rate that changes by date reads date by date.
          const showUnitBreakdown =
            !!quotedTier && !quotedTier.isPricedPerDuration;
          const datedRates =
            quotedTier && quotedTier.pricePerUnit === null
              ? (cartItemTotalDetails.serverQuote?.pricedUnits ?? [])
                  .filter((unit) => unit.priceTierName === priceTier.name)
                  .map((unit) => ({
                    amount: unit.amount,
                    date: unit.date,
                    pricePerUnit: unit.pricePerUnit,
                  }))
              : undefined;

          return (
            <Flex
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
              }}
              key={priceTier.name}
            >
              <DimmedText
                size={14}
                className="cart-summary-item-price"
                data-testid="cart-summary-item-price"
              >
                {t(priceTier.name)}
              </DimmedText>
              <Flex style={{ flexDirection: 'column', alignItems: 'end' }}>
                <CartSummaryItemPrice
                  hidePrice={listing.hidePrice}
                  isPrivateBooking={cartItemTotalDetails.isPrivateBooking}
                  price={priceTier.price}
                  selectedNumber={priceTier.selectedNumber}
                  isPayingWithPoints={isPayingWithPoints}
                  taxes={listing.taxes}
                />
                {showUnitBreakdown && (
                  <CartSummaryItemUnitBreakdown
                    datedRates={datedRates}
                    durationMinutes={durationMinutes}
                    hidePrice={listing.hidePrice}
                    pricePerUnit={quotedTier.pricePerUnit ?? 0}
                    timeFrame={timeFrame}
                    units={quotedTier.units}
                  />
                )}
              </Flex>
            </Flex>
          );
        })}
        {cartItem.addOns?.map((addOn) => (
          <CartItemAddOns
            addOn={addOn}
            hidePrice={listing.hidePrice}
            isPayingWithPoints={isPayingWithPoints}
          />
        ))}
        {cartItemErrorMessage && (
          <CartError>
            <ErrorContainer>
              <Error style={{ whiteSpace: 'pre-line' }}>
                {cartItemErrorMessage.error.message}
              </Error>
            </ErrorContainer>
          </CartError>
        )}
      </Flex>
    );
  };

  return (
    <div>
      <Flex
        direction="row"
        className="cart-summary-item-title-container"
        gap={4}
      >
        <CartSummaryItemTitle className="cart-summary-item-title">
          {cartItem.groupTitle || listing.title}
        </CartSummaryItemTitle>
        {canDelete && (
          <ClearButton
            onClick={remove}
            aria-label={t('deleteReservation', {
              experienceTitle: cartItem.groupTitle || listing.title,
            })}
          >
            <TrashIcon />
          </ClearButton>
        )}
      </Flex>
      <CartSummaryItemSection justifyContent="normal" gap={24}>
        {isHyattEligible ? (
          <HyattBrandFlag scale={3} />
        ) : (
          <ResponsiveImage
            uriObject={getListingCoverUriObject(listing)}
            defaultSize="384w"
            CustomRenderer={ExperienceImage}
            alt={coverAltText || listing.title}
            viewType="small"
          />
        )}

        <Flex
          direction="column"
          gap={8}
          alignItem="initial"
          className="cart-summary-item-date-time-container"
        >
          {isDayBased ? (
            // A stay reads start to end with both dates, and its length in nights or days.
            <>
              <DimmedText
                size={16}
                className="cart-summary-item-date"
                data-testid="cart-summary-item-date"
              >
                {formatDateForDisplay(cartItem.sessionDateTime, locale)}{' '}
                {formatTimeInAMPM(cartItem.sessionDateTime, locale)}
                {' – '}
                {formatDateForDisplay(endDateTime, locale)}{' '}
                {formatTimeInAMPM(endDateTime, locale)}
              </DimmedText>
              <DimmedText
                size={16}
                className="cart-summary-item-time"
                data-testid="cart-summary-item-time"
              >
                {dayUnitsLabel}
              </DimmedText>
            </>
          ) : (
            <>
              <DimmedText
                size={16}
                className="cart-summary-item-date"
                data-testid="cart-summary-item-date"
              >
                {formatDateForDisplay(cartItem.sessionDateTime, locale)}
              </DimmedText>
              <Flex direction="row" gap={8} alignItem="initial">
                <DimmedText
                  size={16}
                  className="cart-summary-item-time"
                  data-testid="cart-summary-item-time"
                >
                  {formatTimeInAMPM(cartItem.sessionDateTime, locale)}
                </DimmedText>
                {isFlexibleDurationEnabled && (
                  <DimmedText
                    size={16}
                    className="cart-summary-item-time"
                    data-testid="cart-summary-item-time"
                  >
                    - {getEndDateTime()}
                  </DimmedText>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </CartSummaryItemSection>
      {renderPrice()}
    </div>
  );
};

export default CartSummaryItem;
